import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';

// Competitor Price Intelligence — ADMIN-ONLY. Compares our menu prices + offers
// against the SAME restaurant's Swiggy/Zomato listing. Never customer-facing.
// Runs in the background (auto-scrape during set IST hours) via a residential
// proxy (datacenter IPs are blocked by Swiggy/Zomato).
function CompetitorIntel() {
  const [tab, setTab] = useState('compare'); // 'compare' | 'listings' | 'settings'
  const [settings, setSettings] = useState(null);
  const [listings, setListings] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => { loadAll(); }, []);
  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, l, r] = await Promise.all([
        adminAPI.ciGetSettings(), adminAPI.ciGetListings(), adminAPI.ciGetRestaurants(),
      ]);
      setSettings(s.data?.data || {});
      setListings(l.data?.data || []);
      setRestaurants(r.data?.data || []);
      setErr('');
    } catch (e) { setErr('Failed to load: ' + (e.response?.data?.message || e.message)); }
    finally { setLoading(false); }
  };

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3500); };

  if (loading) return <div style={s.wrap}><p style={{ color: '#64748B' }}>Loading…</p></div>;

  return (
    <div style={s.wrap}>
      <h1 style={s.title}>Competitor Price Intelligence</h1>
      <p style={s.sub}>Background comparison of our menu prices &amp; offers vs. the <b>same restaurant's</b> Swiggy/Zomato listing. <b>Admin-only — never shown to customers.</b></p>

      {settings && !settings.enabled ? (
        <div style={s.warnBanner}>Auto-scraping is <b>OFF</b>. You can still map listings &amp; run manual scrapes any time; enable it under <b>Settings</b> for scheduled background updates.</div>
      ) : null}
      <div style={s.infoBanner}>ℹ️ <b>Swiggy</b> works with <b>no proxy</b> (mobile API). <b>Zomato blocks our servers</b> at its firewall — it needs a residential proxy set under Settings, and you map it by pasting the restaurant's Zomato URL.</div>

      <div style={s.tabs}>
        {['winloss', 'coupons', 'compare', 'fullmenu', 'listings', 'settings'].map((t) => (
          <button key={t} onClick={() => setTab(t)} style={{ ...s.tab, ...(tab === t ? s.tabActive : {}) }}>
            {t === 'winloss' ? '🏁 Win / Loss' : t === 'coupons' ? '🎟️ Tomato Coupons' : t === 'compare' ? 'Comparison' : t === 'fullmenu' ? 'Full Menu (raw)' : t === 'listings' ? 'Mapped Listings' : 'Settings'}
          </button>
        ))}
      </div>

      {msg ? <div style={s.okBanner}>{msg}</div> : null}
      {err ? <div style={s.errBanner}>{err}</div> : null}

      {tab === 'settings' ? <SettingsTab settings={settings} setSettings={setSettings} flash={flash} setErr={setErr} /> : null}
      {tab === 'listings' ? <ListingsTab listings={listings} restaurants={restaurants} reload={loadAll} flash={flash} setErr={setErr} /> : null}
      {tab === 'fullmenu' ? <FullMenuTab listings={listings} setErr={setErr} /> : null}
      {tab === 'compare' ? <CompareTab listings={listings} restaurants={restaurants} setErr={setErr} /> : null}
      {tab === 'winloss' ? <WinLossTab setErr={setErr} /> : null}
      {tab === 'coupons' ? <CouponsTab listings={listings} /> : null}
    </div>
  );
}

// 🎟️ Tomato Coupons — every mapped restaurant's captured Tomato (Zomato) app coupons.
function CouponsTab({ listings }) {
  const [q, setQ] = useState('');
  const [onlyWith, setOnlyWith] = useState(false);

  const norm = (x) => String(x || '').toLowerCase();
  const zrows = (listings || []).filter((l) => l.platform === 'zomato');
  const totalCoupons = zrows.reduce((n, l) => n + ((l.manualOffers || []).length), 0);
  const withCoupons = zrows.filter((l) => (l.manualOffers || []).length).length;

  let data = zrows.map((l) => ({
    id: l._id,
    name: l.restaurantName || '(unnamed)',
    url: l.externalUrl || (String(l.externalId || '').startsWith('http') ? l.externalId : ''),
    coupons: l.manualOffers || [],
    scraped: l.restaurantOffers || [],
    ingestAt: l.offerIngestLastAt || null,
  }));
  if (onlyWith) data = data.filter((d) => d.coupons.length || d.scraped.length);
  const term = norm(q).trim();
  if (term) {
    data = data.filter((d) => norm(d.name).includes(term)
      || d.coupons.some((c) => norm(c.code).includes(term) || norm(c.label).includes(term))
      || d.scraped.some((sct) => norm(sct).includes(term)));
  }
  data.sort((a, b) => (b.coupons.length - a.coupons.length) || a.name.localeCompare(b.name));

  const humanize = (c) => c.label
    || ((c.kind === 'percent' ? `${c.value}% OFF${c.cap ? ` up to ₹${c.cap}` : ''}` : `Flat ₹${c.value} OFF`) + (c.minOrder ? ` above ₹${c.minOrder}` : ''));
  const chip = (c, i) => (
    <span key={i} title={humanize(c)} style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, padding: '3px 9px', borderRadius: 999,
      background: c.conditional ? '#FEF3C7' : '#EDE9FE', color: c.conditional ? '#92400E' : '#5B21B6',
      border: `1px solid ${c.conditional ? '#FDE68A' : '#DDD6FE'}`, margin: '2px 4px 2px 0', whiteSpace: 'nowrap',
    }}>
      {c.code ? <b style={{ fontFamily: 'ui-monospace, monospace' }}>{c.code}</b> : null}
      <span>{humanize(c)}</span>
      {c.conditional ? <span style={{ fontSize: 10, opacity: 0.8 }}>card/bank</span> : null}
    </span>
  );

  return (
    <div>
      <div style={{ ...s.infoBanner, background: '#F5F3FF', borderColor: '#DDD6FE', color: '#5B21B6' }}>
        🎟️ Every mapped restaurant's <b>Tomato coupons</b> (captured from the Tomato app). <b>{withCoupons}</b> of {zrows.length} restaurants
        have coupons · <b>{totalCoupons}</b> coupons total. Purple = auto-applied cart coupon · amber = card/bank-conditional.
      </div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', margin: '0 0 12px', flexWrap: 'wrap' }}>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search restaurant or coupon code…"
          style={{ flex: '1 1 260px', padding: '9px 12px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 14 }} />
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#334155' }}>
          <input type="checkbox" checked={onlyWith} onChange={(e) => setOnlyWith(e.target.checked)} /> Only restaurants with coupons
        </label>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#F8FAFC', textAlign: 'left' }}>
              <th style={{ ...th, width: '26%' }}>Restaurant</th>
              <th style={th}>Tomato coupons</th>
            </tr>
          </thead>
          <tbody>
            {data.map((d) => (
              <tr key={d.id} style={{ borderBottom: '1px solid #EEF2F7' }}>
                <td style={{ ...td, fontWeight: 700 }}>
                  {d.url ? <a href={d.url} target="_blank" rel="noreferrer" style={{ color: '#0F172A', textDecoration: 'none' }}>{d.name} ↗</a> : d.name}
                  <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 400 }}>{d.coupons.length} coupon{d.coupons.length === 1 ? '' : 's'}</div>
                </td>
                <td style={td}>
                  {d.coupons.length ? <div style={{ display: 'flex', flexWrap: 'wrap' }}>{d.coupons.map(chip)}</div> : null}
                  {!d.coupons.length && d.scraped.length ? <div style={{ fontSize: 12, color: '#64748B' }}>{d.scraped.join(' · ')}</div> : null}
                  {!d.coupons.length && !d.scraped.length ? <span style={{ color: '#cbd5e1' }}>no coupons captured</span> : null}
                </td>
              </tr>
            ))}
            {data.length === 0 ? <tr><td style={td} colSpan={2}><span style={{ color: '#94a3b8' }}>No restaurants match.</span></td></tr> : null}
          </tbody>
        </table>
      </div>
      <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 8 }}>
        Coupons are captured from the Tomato app by the offer scraper and refreshed on each run. Re-run the capture to update, then they appear here and feed the Win/Loss comparison.
      </div>
    </div>
  );
}

// ---- Settings tab ----
function SettingsTab({ settings, setSettings, flash, setErr }) {
  const [cfg, setCfg] = useState({
    enabled: !!settings.enabled,
    platforms: settings.platforms || ['swiggy'],
    proxyUrl: settings.proxyUrl || '',
    scrapeHours: (settings.scrapeHours || [10, 20]).join(', '),
    politeDelayMs: settings.politeDelayMs || 4000,
    zomatoCookie: settings.zomatoCookie || '',
    fees: settings.fees || { swiggy: {}, zomato: {} },
    competitorMatchDiscount: settings.competitorMatchDiscount || { enabled: false, ratioPercent: 50, beatByRupees: 10, perOrderCapRupees: 75, label: 'Special Discount' },
    competitorDeliveryFloor: settings.competitorDeliveryFloor || { enabled: false, floorRupees: 20 },
  });
  const [saving, setSaving] = useState(false);

  const togglePlatform = (p) => setCfg((c) => ({ ...c, platforms: c.platforms.includes(p) ? c.platforms.filter((x) => x !== p) : [...c.platforms, p] }));
  const setFee = (platform, key, val) => setCfg((c) => ({ ...c, fees: { ...c.fees, [platform]: { ...(c.fees[platform] || {}), [key]: val } } }));
  const setCmd = (key, val) => setCfg((c) => ({ ...c, competitorMatchDiscount: { ...(c.competitorMatchDiscount || {}), [key]: val } }));
  const setCdf = (key, val) => setCfg((c) => ({ ...c, competitorDeliveryFloor: { ...(c.competitorDeliveryFloor || {}), [key]: val } }));
  const FEE_FIELDS = [
    ['platformFee', 'Platform fee ₹ (flat, incl GST)'],
    ['packagingFlat', 'Packaging ₹ (flat)'],
    ['packagingPercent', 'Packaging % of items'],
    ['deliveryBase', 'Delivery base ₹'],
    ['deliveryPerKm', 'Delivery ₹ / km'],
    ['deliveryFreeAbove', 'Free delivery above ₹ (0 = never)'],
    ['foodGstPercent', 'Food GST %'],
  ];

  const [testing, setTesting] = useState(false);
  const [proxyResult, setProxyResult] = useState(null);

  const save = async () => {
    setSaving(true); setErr('');
    try {
      const hours = cfg.scrapeHours.split(',').map((x) => parseInt(x.trim(), 10)).filter((x) => x >= 0 && x <= 23);
      const cmd = cfg.competitorMatchDiscount || {};
      const cdf = cfg.competitorDeliveryFloor || {};
      const res = await adminAPI.ciUpdateSettings({ enabled: cfg.enabled, platforms: cfg.platforms, proxyUrl: cfg.proxyUrl, scrapeHours: hours, politeDelayMs: Number(cfg.politeDelayMs), zomatoCookie: cfg.zomatoCookie, fees: cfg.fees, competitorMatchDiscount: { enabled: !!cmd.enabled, ratioPercent: Number(cmd.ratioPercent), beatByRupees: Number(cmd.beatByRupees), perOrderCapRupees: Number(cmd.perOrderCapRupees), label: cmd.label }, competitorDeliveryFloor: { enabled: !!cdf.enabled, floorRupees: Number(cdf.floorRupees) } });
      setSettings(res.data?.data || {});
      flash('Settings saved ✓');
    } catch (e) { setErr('Save failed: ' + (e.response?.data?.message || e.message)); }
    finally { setSaving(false); }
  };

  // Test where the (saved) proxy exits — save first if you just edited it.
  const testProxy = async () => {
    setTesting(true); setErr(''); setProxyResult(null);
    try {
      const res = await adminAPI.ciTestProxy();
      setProxyResult(res.data?.data || null);
    } catch (e) { setErr('Proxy test failed: ' + (e.response?.data?.message || e.message)); }
    finally { setTesting(false); }
  };

  return (
    <div style={s.card}>
      <label style={s.row}>
        <span><b>Enable auto-scraping</b><div style={s.hint}>Off = manual scrapes only. On = background scrapes during the hours below.</div></span>
        <input type="checkbox" checked={cfg.enabled} onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })} style={s.chk} />
      </label>
      <div style={{ ...s.row, display: 'block' }}>
        <div><b>Platforms</b><div style={s.hint}>Swiggy works without a proxy. Zomato needs a residential proxy (below).</div></div>
        <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
          {['swiggy', 'zomato'].map((p) => (
            <label key={p} style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer', textTransform: 'capitalize' }}>
              <input type="checkbox" checked={cfg.platforms.includes(p)} onChange={() => togglePlatform(p)} /> {p}{p === 'zomato' ? ' (needs proxy)' : ''}
            </label>
          ))}
        </div>
      </div>
      <div style={{ ...s.row, display: 'block' }}>
        <div><b>Residential proxy URL</b> <span style={{ color: '#94A3B8', fontWeight: 400 }}>(optional for Swiggy · required for Zomato)</span><div style={s.hint}>Swiggy works without it. Zomato blocks our servers, so it needs a residential proxy here. Format: <code>http://user:pass@host:port</code>.</div></div>
        <input value={cfg.proxyUrl} onChange={(e) => setCfg({ ...cfg, proxyUrl: e.target.value })} placeholder="http://user:pass@host:port" style={s.input} />
      </div>
      <div style={{ ...s.row, display: 'block' }}>
        <div><b>Auto-scrape hours (IST)</b><div style={s.hint}>Comma-separated 0–23. Scrapes once per listed hour. e.g. <code>10, 20</code> = 10am &amp; 8pm.</div></div>
        <input value={cfg.scrapeHours} onChange={(e) => setCfg({ ...cfg, scrapeHours: e.target.value })} placeholder="10, 20" style={s.input} />
      </div>
      <div style={{ ...s.row, display: 'block' }}>
        <div><b>Zomato login cookie</b> <span style={{ color: '#94A3B8', fontWeight: 400 }}>(unlocks login-gated prices)</span><div style={s.hint}>Some Zomato restaurants hide prices unless signed in. Log into Zomato in a browser (use a <b>throwaway account</b> — automated access can get it flagged), open DevTools → Network → any zomato.com request → copy the full <code>Cookie</code> request header and paste it here. Expires periodically; re-paste when Zomato scrapes start reporting "login-gated".</div></div>
        <textarea value={cfg.zomatoCookie} onChange={(e) => setCfg({ ...cfg, zomatoCookie: e.target.value })} placeholder="(paste your logged-in Zomato Cookie header)" style={{ ...s.input, minHeight: 60, fontFamily: 'monospace', fontSize: 12 }} />
      </div>
      <div style={{ ...s.row, display: 'block', borderBottom: 'none' }}>
        <div><b>Polite delay between requests (ms)</b><div style={s.hint}>Spacing between listings so we stay gentle. 1000–60000.</div></div>
        <input type="number" value={cfg.politeDelayMs} onChange={(e) => setCfg({ ...cfg, politeDelayMs: e.target.value })} min={1000} max={60000} style={s.input} />
      </div>
      <div style={{ ...s.row, display: 'block', borderBottom: 'none' }}>
        <div><b>Estimated all-in bill assumptions</b><div style={s.hint}>Used only by the per-order <b>"estimated all-in"</b> comparison in an order's detail popup (item price + these fees). Platform fee is flat per order (incl GST). Delivery uses the order's <b>real distance</b>: base + per-km, waived when the item subtotal ≥ "free above". These are estimates — per-user coupons / membership waivers aren't included. Defaults: Swiggy/Zomato platform ≈ ₹17.58, food GST 5%; set packaging &amp; delivery to match what you see on their apps.</div></div>
        {['swiggy', 'zomato'].map((p) => (
          <div key={p} style={{ marginTop: 10, padding: 10, border: '1px solid #E2E8F0', borderRadius: 8 }}>
            <div style={{ fontWeight: 700, textTransform: 'capitalize', marginBottom: 8 }}>{p}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr))', gap: 8 }}>
              {FEE_FIELDS.map(([k, label]) => (
                <label key={k} style={{ fontSize: 12 }}>
                  <div style={{ color: '#64748B', marginBottom: 2 }}>{label}</div>
                  <input type="number" step="0.01" min="0" value={cfg.fees?.[p]?.[k] ?? 0} onChange={(e) => setFee(p, k, e.target.value)} style={s.input} />
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div style={{ ...s.row, display: 'block' }}>
        <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <span><b>🎯 "Beat Zomato" auto-discount</b><div style={s.hint}>When a customer's cart total (incl. our delivery) is higher than Zomato's best-offer total (Zomato delivery taken as ₹0) and <b>every item matches</b> a Zomato item, auto-apply a <b>platform-funded</b> discount so we come out cheaper. GST, the restaurant payout and the rider payout are untouched — FoodWinc absorbs it. Needs fresh Zomato offers (the app-offer scraper). Shows on the customer bill as the label below — <b>never names Zomato</b>.</div></span>
          <input type="checkbox" checked={!!cfg.competitorMatchDiscount?.enabled} onChange={(e) => setCmd('enabled', e.target.checked)} style={s.chk} />
        </label>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(180px,1fr))', gap: 8, marginTop: 10, opacity: cfg.competitorMatchDiscount?.enabled ? 1 : 0.5 }}>
          <label style={{ fontSize: 12 }}>
            <div style={{ color: '#64748B', marginBottom: 2 }}>Undercut Zomato by ₹</div>
            <input type="number" step="1" min="0" value={cfg.competitorMatchDiscount?.beatByRupees ?? 10} onChange={(e) => setCmd('beatByRupees', e.target.value)} style={s.input} />
          </label>
          <label style={{ fontSize: 12 }}>
            <div style={{ color: '#64748B', marginBottom: 2 }}>Max discount / order ₹ (0 = no cap)</div>
            <input type="number" step="1" min="0" value={cfg.competitorMatchDiscount?.perOrderCapRupees ?? 75} onChange={(e) => setCmd('perOrderCapRupees', e.target.value)} style={s.input} />
          </label>
          <label style={{ fontSize: 12 }}>
            <div style={{ color: '#64748B', marginBottom: 2 }}>Bill label (customer sees)</div>
            <input type="text" maxLength={40} value={cfg.competitorMatchDiscount?.label ?? 'Special Discount'} onChange={(e) => setCmd('label', e.target.value)} style={s.input} />
          </label>
        </div>
        <div style={{ ...s.hint, marginTop: 8 }}>Discount = <b>enough to come out ₹{cfg.competitorMatchDiscount?.beatByRupees ?? 10} cheaper than Zomato's best-offer total</b>{Number(cfg.competitorMatchDiscount?.perOrderCapRupees) > 0 ? `, capped at ₹${cfg.competitorMatchDiscount?.perOrderCapRupees}/order (a big gap can hit the cap and not fully win)` : ''}. Only fires when Zomato has an active offer and every cart item matches. ⚠️ Turn ON only after the user app showing the discount line has shipped.</div>
      </div>
      <div style={{ ...s.row, display: 'block' }}>
        <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <span><b>🚚 "Beat Zomato" delivery-fee floor</b><div style={s.hint}>The <b>complement</b> of the auto-discount above: when Zomato is <b>NOT</b> running any offer/item-discount (so the discount can't fire) but our all-in total is <b>still higher</b> than Zomato's on a full item match, drop <b>our delivery fee</b> to the floor below — but <b>only when that actually makes us win</b> (never gives up delivery margin on a cart we'd still lose). Platform-funded: the rider is still paid for the real distance; FoodWinc just collects less delivery. No app change needed — the customer simply sees the lower delivery fee. Mutually exclusive with the auto-discount.</div></span>
          <input type="checkbox" checked={!!cfg.competitorDeliveryFloor?.enabled} onChange={(e) => setCdf('enabled', e.target.checked)} style={s.chk} />
        </label>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(180px,1fr))', gap: 8, marginTop: 10, opacity: cfg.competitorDeliveryFloor?.enabled ? 1 : 0.5 }}>
          <label style={{ fontSize: 12 }}>
            <div style={{ color: '#64748B', marginBottom: 2 }}>Drop delivery fee to ₹</div>
            <input type="number" step="1" min="0" value={cfg.competitorDeliveryFloor?.floorRupees ?? 20} onChange={(e) => setCdf('floorRupees', e.target.value)} style={s.input} />
          </label>
        </div>
      </div>
      <div style={{ marginTop: 16, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <button onClick={save} disabled={saving} style={s.btn}>{saving ? 'Saving…' : 'Save settings'}</button>
        <button onClick={testProxy} disabled={testing} style={s.btnGhost}>{testing ? 'Testing…' : '📍 Test proxy location'}</button>
      </div>
      {proxyResult ? (
        <div style={{ ...(proxyResult.proxyConfigured ? s.okBanner : s.warnBanner), marginTop: 12, marginBottom: 0 }}>
          {proxyResult.proxyConfigured
            ? <>Requests exit as <b>{proxyResult.ip}</b> · <b>{[proxyResult.city, proxyResult.region, proxyResult.country].filter(Boolean).join(', ')}</b>{proxyResult.isp ? ' · ' + proxyResult.isp : ''}. For a restaurant's full delivery menu, this city should be inside its Zomato delivery zone.</>
            : <>⚠️ <b>No proxy set</b> — requests exit as the server's own IP <b>{proxyResult.ip}</b> ({[proxyResult.city, proxyResult.country].filter(Boolean).join(', ')}). Fine for Swiggy; Zomato needs a residential proxy.</>}
        </div>
      ) : null}
      {settings.lastAutoScrapeSlot ? <div style={{ ...s.hint, marginTop: 12 }}>Last auto-scrape slot: {settings.lastAutoScrapeSlot} (IST)</div> : null}
    </div>
  );
}

// ---- Listings tab (map our restaurant → their listing) ----
function ListingsTab({ listings, restaurants, reload, flash, setErr }) {
  const [restaurantId, setRestaurantId] = useState('');
  const [platform, setPlatform] = useState('swiggy');
  const [externalId, setExternalId] = useState('');
  const [externalUrl, setExternalUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [candidates, setCandidates] = useState(null);
  const [scraping, setScraping] = useState('');
  // Manual offers editor (per listing) — used in the "Compare on Zomato/Swiggy" panel.
  const emptyOffer = { kind: 'percent', value: '', cap: '', minOrder: '', code: '', conditional: false, label: '' };
  const [offersFor, setOffersFor] = useState(null);
  const [offersDraft, setOffersDraft] = useState([]);
  const [newOffer, setNewOffer] = useState(emptyOffer);
  const [savingOffers, setSavingOffers] = useState(false);

  const openOffers = (l) => { setOffersFor(l); setOffersDraft((l.manualOffers || []).map((o) => ({ ...o }))); setNewOffer(emptyOffer); };
  const addOfferToDraft = () => {
    const value = Number(newOffer.value) || 0;
    if (value <= 0) { setErr('Enter a discount value first'); return; }
    setOffersDraft((d) => [...d, {
      kind: newOffer.kind === 'flat' ? 'flat' : 'percent', value,
      cap: Number(newOffer.cap) || 0, minOrder: Number(newOffer.minOrder) || 0,
      code: (newOffer.code || '').trim(), conditional: !!newOffer.conditional, label: (newOffer.label || '').trim(),
    }]);
    setNewOffer(emptyOffer); setErr('');
  };
  const saveOffers = async () => {
    setSavingOffers(true); setErr('');
    try {
      await adminAPI.ciSetListingOffers(offersFor._id, offersDraft);
      flash(`Saved ${offersDraft.length} offer(s) ✓`); setOffersFor(null); reload();
    } catch (e) { setErr('Save failed: ' + (e.response?.data?.message || e.message)); }
    finally { setSavingOffers(false); }
  };
  const offerText = (o) => (o.label && o.label.trim()) || ((o.kind === 'flat' ? `Flat ₹${o.value} OFF` : `${o.value}% OFF${o.cap ? ` up to ₹${o.cap}` : ''}`) + (o.minOrder ? ` above ₹${o.minOrder}` : '') + (o.code ? ` · ${o.code}` : ''));

  const add = async () => {
    if (!restaurantId) { setErr('Pick one of our restaurants first'); return; }
    setBusy(true); setErr('');
    try {
      await adminAPI.ciUpsertListing({ restaurantId, platform, externalId, externalUrl });
      setExternalId(''); setExternalUrl(''); setCandidates(null);
      flash('Listing saved ✓');
      reload();
    } catch (e) { setErr('Save failed: ' + (e.response?.data?.message || e.message)); }
    finally { setBusy(false); }
  };

  const autoFind = async () => {
    if (!restaurantId) { setErr('Pick a restaurant first'); return; }
    setBusy(true); setErr(''); setCandidates(null);
    try {
      const res = await adminAPI.ciFindExternal({ restaurantId, platform });
      setCandidates(res.data?.data || []);
      if (!(res.data?.data || []).length) setErr('No match found on Swiggy for this name. Try pasting the Swiggy restaurantId manually (open the restaurant on swiggy.com — the ID is in the URL).');
    } catch (e) { setErr('Find failed: ' + (e.response?.data?.message || e.message)); }
    finally { setBusy(false); }
  };

  const scrapeNow = async (id) => {
    setScraping(id); setErr('');
    try {
      const res = await adminAPI.ciScrapeOne(id);
      const d = res.data?.data || {};
      flash(res.data?.success ? `Scraped ${d.itemCount || 0} items ✓` : `Scrape failed: ${d.error || 'unknown'}`);
      reload();
    } catch (e) { setErr('Scrape failed: ' + (e.response?.data?.message || e.message)); }
    finally { setScraping(''); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this mapping and its cached data?')) return;
    try { await adminAPI.ciDeleteListing(id); flash('Deleted'); reload(); }
    catch (e) { setErr('Delete failed: ' + (e.response?.data?.message || e.message)); }
  };

  const fmt = (d) => d ? new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

  return (
    <div>
      <div style={s.card}>
        <div style={{ fontWeight: 700, marginBottom: 12 }}>Map a restaurant to a competitor listing</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 130px', gap: 12 }}>
          <select value={restaurantId} onChange={(e) => setRestaurantId(e.target.value)} style={s.input}>
            <option value="">— Select our restaurant —</option>
            {restaurants.map((r) => <option key={r._id} value={r._id} disabled={!r.hasCoords}>{r.name}{r.hasCoords ? '' : ' (no coords)'}</option>)}
          </select>
          <select value={platform} onChange={(e) => setPlatform(e.target.value)} style={s.input}>
            <option value="swiggy">Swiggy</option>
            <option value="zomato">Zomato</option>
          </select>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: platform === 'zomato' ? '1fr' : '1fr 1fr', gap: 12, marginTop: 12 }}>
          <input value={externalId} onChange={(e) => setExternalId(e.target.value)} placeholder={platform === 'zomato' ? 'Paste the Zomato restaurant URL (e.g. https://www.zomato.com/agra/ajanta-sweets/order)' : 'Their restaurant ID (e.g. Swiggy restaurantId)'} style={s.input} />
          {platform !== 'zomato' ? <input value={externalUrl} onChange={(e) => setExternalUrl(e.target.value)} placeholder="Their listing URL (optional)" style={s.input} /> : null}
        </div>
        {platform === 'zomato' ? <div style={{ ...s.hint, marginTop: 8 }}>Auto-find searches Zomato by name (delivery listings only). Or paste the Order Online URL yourself. Zomato needs the residential proxy (Settings).</div> : null}
        <div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
          <button onClick={add} disabled={busy} style={s.btn}>{busy ? '…' : 'Save mapping'}</button>
          <button onClick={autoFind} disabled={busy} style={s.btnGhost}>🔎 Auto-find on {platform === 'zomato' ? 'Zomato' : 'Swiggy'}</button>
        </div>
        {candidates ? (
          <div style={{ marginTop: 12 }}>
            <div style={s.hint}>Matches — click one to use it:</div>
            {candidates.map((c) => (
              <button key={c.externalId} onClick={() => setExternalId(c.externalId)} style={s.candidate}>
                <b>{c.name}</b>{c.areaName ? ' · ' + c.areaName : ''} · match {Math.round(c.score * 100)}%
                <div style={{ ...s.hint, marginTop: 2, wordBreak: 'break-all' }}>{c.externalId}</div>
              </button>
            ))}
          </div>
        ) : null}
      </div>

      <div style={{ ...s.card, marginTop: 16 }}>
        <div style={{ fontWeight: 700, marginBottom: 12 }}>Mapped listings ({listings.length})</div>
        {!listings.length ? <p style={s.hint}>None yet.</p> : (
          <table style={s.table}>
            <thead><tr>
              <th style={s.th}>Restaurant</th><th style={s.th}>Platform</th><th style={s.th}>Ext. ID</th>
              <th style={s.th}>Items</th><th style={s.th}>Last scraped</th><th style={s.th}>Status</th><th style={s.th}></th>
            </tr></thead>
            <tbody>
              {listings.map((l) => (
                <tr key={l._id}>
                  <td style={s.td}>{l.restaurantName}</td>
                  <td style={{ ...s.td, textTransform: 'capitalize' }}>{l.platform}</td>
                  <td style={s.td}>{l.externalId || <span style={{ color: '#DC2626' }}>unmapped</span>}</td>
                  <td style={s.td}>{(l.items || []).length}</td>
                  <td style={s.td}>{fmt(l.lastScrapedAt)}</td>
                  <td style={s.td}>
                    <span style={{ ...s.badge, ...(l.lastStatus === 'ok' ? s.badgeOk : l.lastStatus === 'error' ? s.badgeErr : s.badgeNeutral) }} title={l.lastError || ''}>
                      {l.lastStatus}{l.lastStatus === 'error' && l.lastError ? ' ⓘ' : ''}
                    </span>
                  </td>
                  <td style={s.td}>
                    <button onClick={() => scrapeNow(l._id)} disabled={scraping === l._id || !l.externalId} style={s.btnSm}>{scraping === l._id ? '…' : 'Scrape'}</button>
                    <button onClick={() => openOffers(l)} style={{ ...s.btnSm, marginLeft: 6, background: (l.manualOffers || []).length ? '#ede9fe' : undefined }} title="Manually enter this restaurant's offers for the comparison">🎟️ Offers ({(l.manualOffers || []).length})</button>
                    <button onClick={() => del(l._id)} style={{ ...s.btnSm, color: '#DC2626', marginLeft: 6 }}>Del</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {offersFor && (
        <div onClick={() => setOffersFor(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '32px 14px', zIndex: 1000, overflowY: 'auto' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 640, boxShadow: '0 20px 60px rgba(0,0,0,0.35)', overflow: 'hidden', marginBottom: 40 }}>
            <div style={{ background: 'linear-gradient(135deg,#7c3aed,#5b21b6)', color: '#fff', padding: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 800 }}>{offersFor.restaurantName} · <span style={{ textTransform: 'capitalize' }}>{offersFor.platform}</span> offers</div>
                <div style={{ fontSize: 12, opacity: 0.9 }}>Enter the offers shown on {offersFor.platform} — they appear in the "Compare on Zomato/Swiggy" panel + the best-case total.</div>
              </div>
              <button onClick={() => setOffersFor(null)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', width: 30, height: 30, borderRadius: 8, fontSize: 16, cursor: 'pointer' }}>✕</button>
            </div>
            <div style={{ padding: 16 }}>
              {offersDraft.length ? offersDraft.map((o, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '8px 10px', border: '1px solid #eee', borderRadius: 8, marginBottom: 6 }}>
                  <span style={{ fontSize: 13 }}>{offerText(o)}{o.conditional ? <span style={{ marginLeft: 6, fontSize: 11, color: '#6b7280' }}>💳 card/bank only</span> : null}</span>
                  <button onClick={() => setOffersDraft((d) => d.filter((_, x) => x !== i))} style={{ ...s.btnSm, color: '#DC2626' }}>Remove</button>
                </div>
              )) : <div style={s.hint}>No offers yet — add the ones shown on {offersFor.platform} for this restaurant below.</div>}

              <div style={{ border: '1px dashed #d1c4f0', borderRadius: 10, padding: 12, marginTop: 10, background: '#faf9ff' }}>
                <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Add an offer</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(130px,1fr))', gap: 8 }}>
                  <label style={{ display: 'flex', flexDirection: 'column', fontSize: 11, color: '#64748B', gap: 3 }}>Type
                    <select value={newOffer.kind} onChange={(e) => setNewOffer({ ...newOffer, kind: e.target.value })} style={s.input}>
                      <option value="percent">% off</option><option value="flat">Flat ₹ off</option>
                    </select>
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column', fontSize: 11, color: '#64748B', gap: 3 }}>{newOffer.kind === 'flat' ? 'Amount ₹' : 'Percent %'}
                    <input type="number" min="0" value={newOffer.value} onChange={(e) => setNewOffer({ ...newOffer, value: e.target.value })} style={s.input} />
                  </label>
                  {newOffer.kind === 'percent' ? (
                    <label style={{ display: 'flex', flexDirection: 'column', fontSize: 11, color: '#64748B', gap: 3 }}>Up to ₹ (cap)
                      <input type="number" min="0" value={newOffer.cap} onChange={(e) => setNewOffer({ ...newOffer, cap: e.target.value })} placeholder="0 = none" style={s.input} />
                    </label>
                  ) : null}
                  <label style={{ display: 'flex', flexDirection: 'column', fontSize: 11, color: '#64748B', gap: 3 }}>Min order ₹
                    <input type="number" min="0" value={newOffer.minOrder} onChange={(e) => setNewOffer({ ...newOffer, minOrder: e.target.value })} placeholder="0 = none" style={s.input} />
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column', fontSize: 11, color: '#64748B', gap: 3 }}>Code (optional)
                    <input value={newOffer.code} onChange={(e) => setNewOffer({ ...newOffer, code: e.target.value })} placeholder="e.g. FLAT150" style={s.input} />
                  </label>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, marginTop: 10, color: '#334155' }}>
                  <input type="checkbox" checked={newOffer.conditional} onChange={(e) => setNewOffer({ ...newOffer, conditional: e.target.checked })} />
                  Card / bank / UPI-specific (shown in the list but <b>not</b> applied to the best-case total)
                </label>
                <button onClick={addOfferToDraft} style={{ ...s.btn, marginTop: 10 }}>+ Add offer</button>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, padding: 16, borderTop: '1px solid #f1f5f9' }}>
              <button onClick={saveOffers} disabled={savingOffers} style={s.btn}>{savingOffers ? 'Saving…' : `💾 Save ${offersDraft.length} offer(s)`}</button>
              <button onClick={() => setOffersFor(null)} style={s.btnGhost}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Compare tab ----
function CompareTab({ listings, restaurants, setErr }) {
  const mapped = listings.filter((l) => l.externalId);
  const [restaurantId, setRestaurantId] = useState(mapped[0]?.restaurantId || '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // The selector lists ALL our restaurants so newly-added ones are never missing
  // here — tagged by competitor-mapping status. Fully-mapped restaurants (a listing
  // WITH an externalId) sort first (the useful default); restaurants with only
  // placeholder listings read "mapping incomplete", never-set-up ones "not mapped"
  // — both point the admin to the Mapped Listings tab. Falls back to the mapped
  // listings if the restaurants list somehow failed to load.
  const mappedIds = new Set(mapped.map((l) => String(l.restaurantId)));
  const listingIds = new Set(listings.map((l) => String(l.restaurantId)));
  const statusOf = (id) => mappedIds.has(id) ? 'mapped' : listingIds.has(id) ? 'partial' : 'none';
  const rank = { mapped: 0, partial: 1, none: 2 };
  const uniq = (restaurants && restaurants.length
    ? restaurants.map((r) => ({ id: String(r._id), name: r.name, status: statusOf(String(r._id)) }))
    : mapped
        .map((l) => ({ id: String(l.restaurantId), name: l.restaurantName, status: 'mapped' }))
        .filter((r, i, a) => a.findIndex((x) => x.id === r.id) === i)
  ).sort((a, b) => (rank[a.status] - rank[b.status]) || String(a.name).localeCompare(String(b.name)));
  const statusLabel = { mapped: '', partial: ' — mapping incomplete', none: ' — not mapped' };
  const selStatus = restaurantId ? statusOf(String(restaurantId)) : null;
  const selName = (uniq.find((r) => r.id === String(restaurantId)) || {}).name || '';

  const load = async (id) => {
    if (!id) return;
    setLoading(true); setErr('');
    try { const res = await adminAPI.ciComparison(id); setData(res.data?.data || null); }
    catch (e) { setErr('Load failed: ' + (e.response?.data?.message || e.message)); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (restaurantId) load(restaurantId); /* eslint-disable-next-line */ }, [restaurantId]);

  const [expanded, setExpanded] = useState({});
  const [viewPlatform, setViewPlatform] = useState('swiggy');
  const toggle = (i) => setExpanded((e) => ({ ...e, [i]: !e[i] }));
  const deltaColor = (d) => d > 0 ? '#DC2626' : d < 0 ? '#166534' : '#64748B';
  const fmtDelta = (d) => d == null ? '—' : (d > 0 ? `+₹${d}` : `₹${d}`);
  // Δ as a % of the competitor's price (+ = we're costlier, − = we're cheaper). Same sign/colour as Δ.
  const dPct = (c) => (c && c.theirPrice) ? Math.round(c.delta / c.theirPrice * 1000) / 10 : null;
  const vPct = (v) => (v && v.theirPrice && v.delta != null) ? Math.round(v.delta / v.theirPrice * 1000) / 10 : null;
  const fmtPct = (p) => p == null ? '' : ` (${p > 0 ? '+' : ''}${p}%)`;
  const cap = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
  const availPlatforms = (data?.platforms || []).map((p) => p.platform);
  const vp = availPlatforms.includes(viewPlatform) ? viewPlatform : (availPlatforms[0] || 'swiggy');
  // Average Δ% across matched items on the current platform (our vs their, % of their price).
  const avgDeltaPct = (() => {
    const arr = (data?.rows || []).map((r) => { const cc = r.competitors[vp]; return (cc && cc.theirPrice) ? cc.delta / cc.theirPrice * 100 : null; }).filter((x) => x != null);
    return arr.length ? Math.round(arr.reduce((a, b) => a + b, 0) / arr.length * 10) / 10 : null;
  })();

  // price-difference direction filter (applies to the table + the export)
  const [direction, setDirection] = useState('all'); // all | up | down | equal
  const [exporting, setExporting] = useState(false);
  const [aiMatching, setAiMatching] = useState(false);
  const [aiMsg, setAiMsg] = useState('');
  const passesDir = (d) => d == null ? false : direction === 'up' ? d > 0 : direction === 'down' ? d < 0 : direction === 'equal' ? d === 0 : true;

  // Photo-import state: apply a competitor's dish photo to our item.
  const [applying, setApplying] = useState({});      // itemId -> bool (in flight)
  const [appliedImg, setAppliedImg] = useState({});  // itemId -> new url (session override)
  const [onlyMissing, setOnlyMissing] = useState(false);
  const curImg = (r) => appliedImg[r.ourItemId] || r.ourImage || '';
  const applyPhoto = async (itemId, imageUrl) => {
    if (!itemId || !imageUrl) return;
    setApplying((a) => ({ ...a, [itemId]: true })); setErr('');
    try {
      const res = await adminAPI.ciApplyPhoto({ menuItemId: itemId, imageUrl });
      const newUrl = res.data?.data?.image;
      if (newUrl) setAppliedImg((m) => ({ ...m, [itemId]: newUrl }));
    } catch (e) { setErr('Apply photo failed: ' + (e.response?.data?.message || e.message)); }
    finally { setApplying((a) => ({ ...a, [itemId]: false })); }
  };

  const visibleRows = (data?.rows || []).filter((r) => {
    if (onlyMissing && curImg(r)) return false;
    if (direction === 'all') return true;
    const c = r.competitors[vp];
    return c && passesDir(c.delta);
  });

  const aiMatch = async () => {
    setAiMatching(true); setErr(''); setAiMsg('');
    try {
      const res = await adminAPI.ciAiMatch(restaurantId);
      setAiMsg(res.data?.message || 'AI matching done.');
      await load(restaurantId);
    } catch (e) { setErr('AI match failed: ' + (e.response?.data?.message || e.message)); }
    finally { setAiMatching(false); }
  };

  const exportExcel = async () => {
    setExporting(true); setErr('');
    try {
      const res = await adminAPI.ciExport(restaurantId, { platform: vp, direction });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(data?.restaurantName || 'restaurant').replace(/[^a-z0-9]+/gi, '_')}_comparison.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { setErr('Export failed: ' + (e.response?.data?.message || e.message)); }
    finally { setExporting(false); }
  };

  return (
    <div>
      <div style={s.card}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={restaurantId} onChange={(e) => setRestaurantId(e.target.value)} style={{ ...s.input, maxWidth: 320, marginTop: 0 }}>
            <option value="">— Select a restaurant —</option>
            {uniq.map((r) => <option key={r.id} value={r.id}>{r.name}{statusLabel[r.status]}</option>)}
          </select>
          <button onClick={() => load(restaurantId)} disabled={!restaurantId || loading} style={s.btnGhost}>{loading ? 'Loading…' : 'Refresh'}</button>
          {data ? (
            <>
              <select value={direction} onChange={(e) => setDirection(e.target.value)} style={{ ...s.input, maxWidth: 210, marginTop: 0 }} title="Filter by price difference">
                <option value="all">All items</option>
                <option value="up">▲ We're costlier (up)</option>
                <option value="down">▼ We're cheaper (down)</option>
                <option value="equal">= Equal price</option>
              </select>
              <button onClick={aiMatch} disabled={aiMatching || !restaurantId} style={{ ...s.btn, background: '#7C3AED' }} title="Use AI (Claude) to match items by meaning — catches spelling/transliteration/wording differences the basic matcher misses (e.g. Sonpapri↔Soan Papdi). Re-run after each scrape.">{aiMatching ? '🤖 AI matching…' : '🤖 AI match'}</button>
              <button onClick={exportExcel} disabled={exporting || !restaurantId} style={s.btn} title={`Swiggy + Zomato in one sheet. The filter above applies to ${cap(vp)}.`}>{exporting ? 'Exporting…' : '⬇ Export Excel (Swiggy + Zomato)'}</button>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#475569', cursor: 'pointer' }} title="Show only our items that have no photo yet — the best candidates to import a competitor photo for.">
                <input type="checkbox" checked={onlyMissing} onChange={(e) => setOnlyMissing(e.target.checked)} /> Only items missing a photo
              </label>
            </>
          ) : null}
        </div>
        {aiMsg ? <p style={{ ...s.hint, marginTop: 10, color: '#6D28D9', fontWeight: 600 }}>🤖 {aiMsg}</p> : null}
        {restaurantId && selStatus !== 'mapped' ? (
          <p style={{ ...s.hint, marginTop: 12 }}><b>{selName || 'This restaurant'}</b> {selStatus === 'partial' ? 'has a competitor listing but no external ID yet' : 'isn’t mapped to a Swiggy/Zomato listing yet'} — set it in the <b>Mapped Listings</b> tab (paste the Swiggy/Zomato ID or URL), then scrape it. Until then the comparison below stays empty.</p>
        ) : (!mapped.length ? <p style={{ ...s.hint, marginTop: 12 }}>Map at least one listing (with an external ID) in the <b>Mapped Listings</b> tab, then scrape it.</p> : null)}
      </div>

      {data ? (
        <>
          <div style={{ ...s.card, marginTop: 16 }}>
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
              <Stat label="Our items" value={data.totalOurItems} />
              <Stat label="Matched" value={data.matchedCount} />
              <Stat label="Comparisons" value={data.summary.comparisons} />
              <Stat label="Avg delta (us − them)" value={`₹${data.summary.avgDelta}`} color={deltaColor(data.summary.avgDelta)} />
              <Stat label="Avg Δ%" value={avgDeltaPct != null ? `${avgDeltaPct > 0 ? '+' : ''}${avgDeltaPct}%` : '—'} color={deltaColor(avgDeltaPct)} />
              <Stat label="We're costlier" value={data.summary.costlierCount} color="#DC2626" />
              <Stat label="We're cheaper" value={data.summary.cheaperCount} color="#166534" />
            </div>
            {data.platforms.map((p) => (
              <div key={p.platform} style={{ marginTop: 12, fontSize: 13, color: '#475569' }}>
                <b style={{ textTransform: 'capitalize' }}>{p.platform}</b>: {p.itemCount} items · {p.lastStatus}
                {p.lastError ? <span style={{ color: '#DC2626' }}> ({p.lastError})</span> : null}
                {p.restaurantOffers && p.restaurantOffers.length ? <div style={s.hint}>Their offers: {p.restaurantOffers.join(' · ')}</div> : null}
              </div>
            ))}
          </div>

          {availPlatforms.length > 1 ? (
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              {availPlatforms.map((p) => (
                <button key={p} onClick={() => setViewPlatform(p)} style={{ ...s.tab, ...(vp === p ? s.tabActive : {}) }}>{cap(p)}</button>
              ))}
            </div>
          ) : null}

          {direction !== 'all' ? <div style={{ ...s.hint, marginTop: 12 }}>Showing {visibleRows.length} {vp} items where {direction === 'up' ? "we're costlier" : direction === 'down' ? "we're cheaper" : 'price is equal'}.</div> : null}
          <div style={{ ...s.card, marginTop: 16, overflowX: 'auto' }}>
            <table style={s.table}>
              <thead><tr>
                <th style={s.th}></th><th style={s.th}>Our item</th><th style={s.th}>Our ₹</th>
                <th style={s.th}>{cap(vp)} item</th><th style={s.th}>{cap(vp)} ₹</th><th style={s.th}>Δ (₹ · %)</th><th style={s.th}>Their offer</th><th style={s.th}>Photo</th>
              </tr></thead>
              <tbody>
                {visibleRows.map((r, i) => {
                  const c = r.competitors[vp];
                  const variantPairs = (c && c.variants) || [];
                  const hasVariants = (r.ourVariants && r.ourVariants.length) || variantPairs.length;
                  const open = !!expanded[i];
                  return (
                    <React.Fragment key={i}>
                      <tr>
                        <td style={{ ...s.td, width: 22, cursor: hasVariants ? 'pointer' : 'default', color: '#94A3B8' }} onClick={() => hasVariants && toggle(i)}>{hasVariants ? (open ? '▾' : '▸') : ''}</td>
                        <td style={s.td}>{r.name}{hasVariants ? <span style={s.hint}> · {(r.ourVariants || []).length || variantPairs.length} variants</span> : null}</td>
                        <td style={s.td}>₹{r.ourPrice}</td>
                        <td style={s.td}>{c ? <span title={`${c.matchedBy === 'ai' ? 'AI match' : 'name match'} ${Math.round(c.matchScore * 100)}%`}>{c.theirName}{c.matchedBy === 'ai' ? <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: '#fff', background: '#7C3AED', padding: '1px 5px', borderRadius: 4 }}>AI</span> : null}</span> : <span style={s.hint}>no match</span>}</td>
                        <td style={s.td}>{c ? `₹${c.theirPrice}` : '—'}</td>
                        <td style={{ ...s.td, color: c ? deltaColor(c.delta) : '#94A3B8', fontWeight: 600 }}>{c ? <>{fmtDelta(c.delta)}<span style={{ fontWeight: 400, fontSize: 11 }}>{fmtPct(dPct(c))}</span></> : '—'}</td>
                        <td style={s.td}>{c && c.theirOffer ? c.theirOffer : '—'}</td>
                        <td style={s.td}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            {curImg(r) ? <img src={curImg(r)} alt="" style={s.thumb} title="Our current photo" /> : <span style={s.hint}>none</span>}
                            {c && c.theirImage ? (
                              <>
                                <img src={c.theirImage} alt="" style={{ ...s.thumb, border: '1px solid #E2E8F0' }} title={`${cap(vp)} photo`} />
                                <button style={s.applyBtn} disabled={!!applying[r.ourItemId]} onClick={() => applyPhoto(r.ourItemId, c.theirImage)} title={`Import this ${cap(vp)} photo onto our item`}>
                                  {applying[r.ourItemId] ? '…' : (curImg(r) ? 'Replace' : 'Use')}
                                </button>
                              </>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                      {open && hasVariants ? (
                        <tr>
                          <td></td>
                          <td colSpan={7} style={{ padding: '4px 10px 12px', background: '#F8FAFC' }}>
                            <div style={{ ...s.hint, marginBottom: 6 }}>Variants (Swiggy prices are raw variation figures — may be additive, treat Δ as approximate):</div>
                            <table style={{ ...s.table, background: 'transparent' }}>
                              <thead><tr>
                                <th style={s.thSm}>Our variant</th><th style={s.thSm}>Our ₹</th><th style={s.thSm}>{cap(vp)} variant</th><th style={s.thSm}>{cap(vp)} ₹</th><th style={s.thSm}>Δ (₹ · %)</th>
                              </tr></thead>
                              <tbody>
                                {variantPairs.length ? variantPairs.map((v, j) => (
                                  <tr key={j}>
                                    <td style={s.tdSm}>{v.ourName || <span style={s.hint}>— (theirs only)</span>}</td>
                                    <td style={s.tdSm}>{v.ourPrice != null ? `₹${v.ourPrice}` : '—'}</td>
                                    <td style={s.tdSm}>{v.theirName ? <span>{v.theirGroup ? <span style={s.hint}>{v.theirGroup}: </span> : null}{v.theirName}</span> : <span style={s.hint}>no match</span>}</td>
                                    <td style={s.tdSm}>{v.theirPrice != null ? `₹${v.theirPrice}` : '—'}</td>
                                    <td style={{ ...s.tdSm, color: v.delta != null ? deltaColor(v.delta) : '#94A3B8', fontWeight: 600 }}>{fmtDelta(v.delta)}<span style={{ fontWeight: 400, fontSize: 11 }}>{fmtPct(vPct(v))}</span></td>
                                  </tr>
                                )) : (r.ourVariants || []).map((v, j) => (
                                  <tr key={j}>
                                    <td style={s.tdSm}>{v.name}</td><td style={s.tdSm}>₹{v.price}</td>
                                    <td style={s.tdSm} colSpan={3}><span style={s.hint}>no {cap(vp)} match for this item</span></td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Unmatched competitor items (BOTH platforms) — prices we couldn't map to
              our menu, shown so nothing is hidden. Stacked so both show at once. */}
          {availPlatforms.map((plat) => ((data.unmatched?.[plat] || []).length ? (
            <div key={plat} style={{ ...s.card, marginTop: 16 }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>Unmatched on {cap(plat)} <span style={s.hint}>· {(data.unmatched[plat] || []).length} items</span></div>
              <p style={{ ...s.hint, marginTop: 0, marginBottom: 10 }}>{cap(plat)} items not matched to any of our menu items (we may not sell them, or no match was found) — their prices are shown so nothing is hidden. Tip: run <b>🤖 AI match</b> to shrink this list.</p>
              <div style={{ maxHeight: 460, overflowY: 'auto' }}>
                <table style={s.table}>
                  <thead><tr><th style={s.th}>{cap(plat)} item</th><th style={s.th}>₹</th><th style={s.th}>Offer</th></tr></thead>
                  <tbody>
                    {(data.unmatched[plat] || []).map((it, i) => (
                      <React.Fragment key={i}>
                        <tr>
                          <td style={s.td}>{it.name}{it.category ? <span style={s.hint}> · {it.category}</span> : null}</td>
                          <td style={s.td}>₹{it.price}</td>
                          <td style={s.td}>{it.offer || '—'}</td>
                        </tr>
                        {(it.variants || []).map((v, j) => (
                          <tr key={`v${j}`}><td style={{ ...s.td, paddingLeft: 22, color: '#64748B' }}>↳ {v.name}</td><td style={{ ...s.td, color: '#64748B' }}>₹{v.price}</td><td style={s.td}></td></tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null))}
        </>
      ) : null}
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div>
      <div style={{ fontSize: 22, fontWeight: 800, color: color || '#0F172A' }}>{value}</div>
      <div style={{ fontSize: 12, color: '#64748B' }}>{label}</div>
    </div>
  );
}

// ---- Full Menu (raw) tab: the COMPLETE scraped menu for one restaurant on each
// platform — every item + variant + price, with NO matching/filtering against our
// own menu (that's the Comparison tab). This is "just show me all their prices".
function FullMenuTab({ listings, setErr }) {
  const mapped = listings.filter((l) => l.externalId);
  const uniq = [];
  const seen = new Set();
  for (const l of mapped) { if (!seen.has(l.restaurantId)) { seen.add(l.restaurantId); uniq.push({ id: l.restaurantId, name: l.restaurantName }); } }
  uniq.sort((a, b) => String(a.name).localeCompare(String(b.name)));

  const [restaurantId, setRestaurantId] = useState(uniq[0]?.id || '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scraping, setScraping] = useState(false);
  const [exporting, setExporting] = useState(false);
  const cap = (x) => x ? x.charAt(0).toUpperCase() + x.slice(1) : x;

  const load = async (id) => {
    if (!id) return;
    setLoading(true); setErr('');
    try { const res = await adminAPI.ciFullMenu(id); setData(res.data?.data || null); }
    catch (e) { setErr('Load failed: ' + (e.response?.data?.message || e.message)); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (restaurantId) load(restaurantId); /* eslint-disable-next-line */ }, [restaurantId]);

  const rescrape = async () => {
    const ids = mapped.filter((l) => l.restaurantId === restaurantId).map((l) => l._id);
    if (!ids.length) return;
    setScraping(true); setErr('');
    try {
      for (const id of ids) { try { await adminAPI.ciScrapeOne(id); } catch (e) { /* keep going for the other platform */ } }
      await load(restaurantId);
    } catch (e) { setErr('Re-scrape failed: ' + (e.response?.data?.message || e.message)); }
    finally { setScraping(false); }
  };

  const exportXlsx = async () => {
    setExporting(true); setErr('');
    try {
      const res = await adminAPI.ciFullMenuExport(restaurantId);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `${(data?.restaurantName || 'restaurant').replace(/[^a-z0-9]+/gi, '_')}_full_menu.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { setErr('Export failed: ' + (e.response?.data?.message || e.message)); }
    finally { setExporting(false); }
  };

  return (
    <div>
      <div style={s.card}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={restaurantId} onChange={(e) => setRestaurantId(e.target.value)} style={{ ...s.input, maxWidth: 320, marginTop: 0 }}>
            <option value="">— Select a mapped restaurant —</option>
            {uniq.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
          <button onClick={() => load(restaurantId)} disabled={!restaurantId || loading} style={s.btnGhost}>{loading ? 'Loading…' : 'Refresh'}</button>
          <button onClick={rescrape} disabled={!restaurantId || scraping} style={s.btnGhost} title="Re-fetch the latest menu from Swiggy + Zomato for this restaurant now">{scraping ? 'Scraping…' : '↻ Re-scrape now'}</button>
          {data ? <button onClick={exportXlsx} disabled={exporting} style={s.btn}>{exporting ? 'Exporting…' : '⬇ Export full menu (Excel)'}</button> : null}
        </div>
        <p style={{ ...s.hint, marginTop: 12 }}>Every item + variant + price scraped from each platform — <b>NOT</b> matched/filtered against our menu. This is the complete competitor menu. (Swiggy variants nest under their item; Zomato size options come through as their own rows.)</p>
      </div>

      {loading ? <p style={{ ...s.hint, marginTop: 16 }}>Loading…</p> : data ? (
        <div style={{ display: 'flex', gap: 16, marginTop: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          {(data.platforms || []).length === 0 ? <p style={s.hint}>No listings mapped for this restaurant yet. Map + scrape it under <b>Mapped Listings</b>.</p> : null}
          {(data.platforms || []).map((p) => (
            <div key={p.platform} style={{ ...s.card, flex: '1 1 420px', minWidth: 340 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
                <div style={{ fontWeight: 700, fontSize: 15 }}>{cap(p.platform)} <span style={s.hint}>· {p.itemCount} items</span></div>
                <div style={s.hint}>{p.lastStatus === 'ok' ? '✓ ok' : p.lastStatus === 'error' ? '⚠ error' : p.lastStatus}{p.lastScrapedAt ? ' · ' + new Date(p.lastScrapedAt).toLocaleString('en-IN') : ''}</div>
              </div>
              {p.lastError ? <div style={{ ...s.hint, color: '#B91C1C', marginTop: 4 }}>{p.lastError}</div> : null}
              {p.restaurantOffers && p.restaurantOffers.length ? <div style={{ ...s.hint, marginTop: 4 }}>Offers: {p.restaurantOffers.join(' · ')}</div> : null}
              <div style={{ maxHeight: 540, overflowY: 'auto', marginTop: 10 }}>
                <table style={s.table}>
                  <thead><tr><th style={s.th}>Item</th><th style={s.th}>₹</th></tr></thead>
                  <tbody>
                    {(p.items || []).length === 0 ? <tr><td style={s.td} colSpan={2}><span style={s.hint}>No items scraped.</span></td></tr> : null}
                    {(p.items || []).map((it, i) => (
                      <React.Fragment key={i}>
                        <tr>
                          <td style={s.td}>{it.name}{it.category ? <span style={s.hint}> · {it.category}</span> : null}{it.offer ? <span style={{ ...s.hint, color: '#166534' }}> · {it.offer}</span> : null}</td>
                          <td style={s.td}>₹{it.price}</td>
                        </tr>
                        {(it.variants || []).map((v, j) => (
                          <tr key={j}>
                            <td style={{ ...s.td, paddingLeft: 22, color: '#64748B' }}>↳ {v.group ? v.group + ': ' : ''}{v.name}</td>
                            <td style={{ ...s.td, color: '#64748B' }}>₹{v.price}</td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// 🏁 Win / Loss — automated FoodWinc vs Swiggy/Zomato final grand total (post best coupon).
function WinLossTab({ setErr }) {
  const [snap, setSnap] = useState(null);
  const [running, setRunning] = useState(false);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);

  const load = async () => {
    try {
      const r = await adminAPI.ciWinLoss();
      setSnap(r.data.data.snapshot);
      setRunning(r.data.data.running);
    } catch (e) { setErr && setErr('Failed to load win/loss'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    if (!running) return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
    /* eslint-disable-next-line */
  }, [running]);

  const run = async () => {
    try { setRunning(true); await adminAPI.ciRunWinLoss(days); setTimeout(load, 1500); }
    catch (e) { setErr && setErr('Failed to start comparison'); setRunning(false); }
  };

  const dstr = (d) => (d == null ? '—' : (d >= 0 ? `+₹${Math.round(d)}` : `−₹${Math.round(Math.abs(d))}`));
  const dcol = (d) => (d == null ? '#64748B' : (d >= 0.5 ? '#16a34a' : (d <= -0.5 ? '#dc2626' : '#64748B')));

  const rows = (snap && snap.restaurants) ? [...snap.restaurants] : [];
  // Biggest problems first: Zomato real-order net-losses desc, then probe losses.
  const lossScore = (r) => {
    const z = r.realOrders && r.realOrders.zomato;
    const real = z ? (z.losses - z.wins) : 0;
    const probe = (r.probe || []).filter((p) => p.platforms && p.platforms.zomato && p.platforms.zomato.result === 'loss').length;
    return real * 10 + probe;
  };
  rows.sort((a, b) => lossScore(b) - lossScore(a));

  const z = snap && snap.summary && snap.summary.zomato;

  const realCell = (ro) => {
    if (!ro) return <span style={{ color: '#94a3b8' }}>no orders</span>;
    return (
      <div style={{ fontSize: 12 }}>
        <b style={{ color: ro.losses > ro.wins ? '#dc2626' : '#16a34a' }}>{ro.winRatePct}% win</b>
        <span style={{ color: '#64748B' }}> ({ro.wins}-{ro.losses}-{ro.ties})</span><br />
        avg <b style={{ color: dcol(ro.avgDelta) }}>{dstr(ro.avgDelta)}</b>
        {ro.worstLoss ? <span style={{ color: '#64748B' }}> · worst #{ro.worstLoss.orderNumber} <b style={{ color: '#dc2626' }}>{dstr(ro.worstLoss.delta)}</b></span> : null}
      </div>
    );
  };

  const probeCell = (r, platform) => {
    const ps = (r.probe || []);
    if (!ps.length) return <span style={{ color: '#94a3b8', fontSize: 12 }}>—</span>;
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {ps.map((p, i) => {
          const v = p.platforms && p.platforms[platform];
          if (!v) return <span key={i} style={{ fontSize: 11, color: '#cbd5e1' }}>₹{p.targetValue}·—</span>;
          const win = v.result === 'win';
          return (
            <span key={i} title={`cart ~₹${p.subtotal} · ours ₹${Math.round(p.ourTotal)} vs ₹${Math.round(v.theirTotal)}${v.bestOffer ? ' (' + (v.bestOffer.text || '') + ')' : ''}`}
              style={{ fontSize: 11, fontWeight: 700, padding: '1px 6px', borderRadius: 10,
                background: win ? 'rgba(22,163,74,0.12)' : (v.result === 'loss' ? 'rgba(220,38,38,0.12)' : '#f1f5f9'),
                color: win ? '#15803d' : (v.result === 'loss' ? '#b91c1c' : '#64748B') }}>
              ₹{p.targetValue} {win ? '✓' : v.result === 'loss' ? '✗' : '='}{dstr(v.delta)}
            </span>
          );
        })}
      </div>
    );
  };

  const freshCell = (r) => {
    const f = (r.freshness && r.freshness.zomato) || null;
    if (!f) return <span style={{ color: '#94a3b8', fontSize: 11 }}>no zomato</span>;
    const stale = f.staleDays != null && f.staleDays > 7;
    const noOffers = (f.offers || 0) === 0;
    return (
      <div style={{ fontSize: 11, color: (stale || noOffers) ? '#b45309' : '#64748B' }}>
        {f.itemCount || 0} items · {f.offers || 0} offers<br />
        {f.staleDays == null ? 'never scraped' : `${f.staleDays}d old`}{(stale || noOffers) ? ' ⚠️' : ''}
      </div>
    );
  };

  return (
    <div>
      <div style={{ ...s.infoBanner, background: '#F0FDFA', borderColor: '#99F6E4', color: '#115E59' }}>
        🏁 Automated: for each restaurant we price your <b>real orders</b> + <b>probe carts</b> on Swiggy/Zomato, apply their <b>best applicable coupon</b>, and compare the <b>final grand total</b> to ours. <b style={{ color: '#16a34a' }}>+green = you're cheaper (WIN)</b>, <b style={{ color: '#dc2626' }}>−red = pricier (LOSS)</b>. Only as accurate as the competitor prices/offers (⚠️ = stale/missing). <b>Scroll down</b> for the date-wise comparison over users' <b>real carts</b> (incl. abandoned ones).
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <label style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>Window:&nbsp;
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} style={{ padding: '6px 8px', borderRadius: 8, border: '1px solid #E2E8F0' }}>
            <option value={1}>Today</option>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
          </select>
        </label>
        <button onClick={run} disabled={running} style={s.btn}>{running ? '⏳ Comparing…' : '▶ Run comparison now'}</button>
        {snap ? <span style={{ fontSize: 12, color: '#64748B' }}>Last run: <b>{snap.date}</b> · {snap.days}d window · {(snap.restaurants || []).length} restaurants</span> : null}
      </div>

      {z ? (
        <div style={{ ...s.okBanner, background: z.losing > 0 ? '#FEF2F2' : '#F0FDF4', borderColor: z.losing > 0 ? '#FECACA' : '#BBF7D0', color: z.losing > 0 ? '#991B1B' : '#166534' }}>
          {z.losing > 0
            ? <>⚠️ Losing to <b>Zomato</b> on <b>{z.losing}</b> of {z.compared} restaurants (real orders) — total overpriced by ~<b>₹{Math.round(z.totalLossRupees)}</b> across those carts.</>
            : <>✅ Winning or tied against <b>Zomato</b> on all {z.compared} compared restaurants (real orders).</>}
        </div>
      ) : null}

      {loading ? <div style={{ padding: 20, color: '#64748B' }}>Loading…</div> : null}
      {!loading && !snap ? <div style={{ padding: 20, color: '#64748B' }}>No comparison yet — click <b>Run comparison now</b>. (Also runs automatically every night.)</div> : null}

      {snap && rows.length ? (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#F8FAFC', textAlign: 'left' }}>
                <th style={th}>Restaurant</th>
                <th style={th}>Zomato — real orders</th>
                <th style={th}>Zomato — probe carts</th>
                <th style={th}>Swiggy — real orders</th>
                <th style={th}>Zomato data</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const zl = r.realOrders && r.realOrders.zomato;
                const losing = zl && zl.losses > zl.wins;
                return (
                  <tr key={r.restaurantId} style={{ borderBottom: '1px solid #EEF2F7', background: losing ? '#FFF7F7' : '#fff' }}>
                    <td style={{ ...td, fontWeight: 700 }}>{r.restaurantName}</td>
                    <td style={td}>{realCell(zl)}</td>
                    <td style={td}>{probeCell(r, 'zomato')}</td>
                    <td style={td}>{realCell(r.realOrders && r.realOrders.swiggy)}</td>
                    <td style={td}>{freshCell(r)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}

      {snap && snap.cartComparison ? <CartComparisonSection cc={snap.cartComparison} dstr={dstr} dcol={dcol} /> : null}
    </div>
  );
}

// 🛒 Date-wise comparison over REAL carts (PendingCart checkout snapshots), split by
// outcome. For each cart: ours (subtotal + delivery + platform + GST) vs Zomato
// (items at Zomato prices + ₹platform + GST − best coupon). Only carts where EVERY
// item matched on Zomato are scored (a fair grand-total compare).
function CartComparisonSection({ cc, dstr, dcol }) {
  const zt = (cc.totals && cc.totals.zomato) || {};
  const ab = zt.abandoned || {}; const pl = zt.placed || {};
  const [filter, setFilter] = useState('loss');
  const [limit, setLimit] = useState(60);
  const allCarts = cc.carts || [];
  const matchFilter = (c) => {
    const z = c.zomato;
    if (filter === 'abandoned') return c.outcome === 'abandoned';
    if (filter === 'loss') return z && z.scored && z.result === 'loss';
    if (filter === 'win') return z && z.scored && z.result === 'win';
    if (filter === 'unmatched') return !z || !z.scored;
    return true; // all
  };
  const carts = allCarts.filter(matchFilter);
  const itemsTip = (c) => (c.items || []).map((i) => `${i.n} x${i.q}: our ₹${Math.round(i.o)}${i.z != null ? ` → Zomato ₹${Math.round(i.z)}` : ' (no Zomato match)'}`).join('\n');
  const cartAgg = (a) => {
    if (!a || !a.scored) return <span style={{ color: '#94a3b8', fontSize: 12 }}>—{a && a.lowCoverage ? ` (${a.lowCoverage} unmatched)` : ''}</span>;
    return (
      <div style={{ fontSize: 12 }}>
        <b style={{ color: a.losses > a.wins ? '#dc2626' : '#16a34a' }}>{a.winRatePct}% win</b>
        <span style={{ color: '#64748B' }}> ({a.wins}-{a.losses}-{a.ties})</span><br />
        avg <b style={{ color: dcol(a.avgDelta) }}>{dstr(a.avgDelta)}</b>
        {a.losses > 0 ? <span style={{ color: '#64748B' }}> · lost ₹{Math.round(a.lossRupees)}</span> : null}
      </div>
    );
  };
  return (
    <div style={{ marginTop: 32 }}>
      <h3 style={{ fontSize: 16, fontWeight: 800, color: '#0F172A', margin: '0 0 8px' }}>🛒 Real carts vs Zomato — date-wise</h3>
      <div style={{ ...s.infoBanner, background: '#F0FDFA', borderColor: '#99F6E4', color: '#115E59' }}>
        From users' actual carts (checkout snapshots, incl. <b>abandoned</b> ones). We price the <b>same items</b> on Zomato
        (items + platform fee + GST − <b>best coupon</b>) and compare the grand total to ours (subtotal + delivery + platform + GST).
        Split by outcome: <b>abandoned</b> = built a cart, didn't order · <b>placed</b> = ordered. Only carts where <b>every item matched</b> on
        Zomato are scored ("unmatched" = couldn't fairly compare).
      </div>

      {ab.scored ? (
        <div style={{ ...s.okBanner, background: ab.losses > ab.wins ? '#FEF2F2' : '#F0FDF4', borderColor: ab.losses > ab.wins ? '#FECACA' : '#BBF7D0', color: ab.losses > ab.wins ? '#991B1B' : '#166534' }}>
          {ab.losses > ab.wins
            ? <>⚠️ On <b>abandoned</b> carts we were <b>pricier than Zomato on {ab.losses} of {ab.scored}</b> ({100 - ab.winRatePct}%) — total ~<b>₹{Math.round(ab.lossRupees)}</b> overpriced. Price may be costing conversions.</>
            : <>✅ On <b>abandoned</b> carts we matched/beat Zomato {ab.wins}/{ab.scored} of the time.</>}
        </div>
      ) : null}

      {/* EVERY CART — one row per cart with its own grand-total comparison */}
      <div style={{ marginTop: 6, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <b style={{ fontSize: 14, color: '#334155' }}>Every cart ({allCarts.length}{cc.cartsCapped ? ` of ${cc.cartsTotal}` : ''}):</b>
        {[['loss', 'Losses'], ['win', 'Wins'], ['abandoned', 'Abandoned'], ['unmatched', 'Unmatched'], ['all', 'All']].map(([k, lbl]) => (
          <button key={k} onClick={() => { setFilter(k); setLimit(60); }}
            style={{ ...s.btnSm, ...(filter === k ? { background: '#2563EB', color: '#fff', borderColor: '#2563EB' } : {}) }}>{lbl}</button>
        ))}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#F8FAFC', textAlign: 'left' }}>
              <th style={th}>Date</th><th style={th}>Restaurant</th><th style={th}>Outcome</th>
              <th style={th}>Items</th><th style={th}>Ours</th><th style={th}>Zomato</th><th style={th}>Δ</th><th style={th}>Their coupon</th>
            </tr>
          </thead>
          <tbody>
            {carts.slice(0, limit).map((c, i) => {
              const zz = c.zomato; const scored = zz && zz.scored; const result = scored ? zz.result : null;
              const bg = result === 'loss' ? '#FFF7F7' : (result === 'win' ? '#F6FEF9' : '#fff');
              return (
                <tr key={i} style={{ borderBottom: '1px solid #EEF2F7', background: bg }}>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{c.date}</td>
                  <td style={{ ...td, fontWeight: 600 }}>{c.restaurantName}</td>
                  <td style={td}>{c.outcome === 'abandoned' ? <span style={{ color: '#b45309', fontWeight: 600 }}>abandoned</span> : 'placed'}</td>
                  <td style={{ ...td, cursor: 'help', color: '#475569', whiteSpace: 'pre' }} title={itemsTip(c)}>{c.totalItems} item{c.totalItems > 1 ? 's' : ''} ⓘ</td>
                  <td style={td}>₹{Math.round(c.ourTotal)}</td>
                  <td style={td}>{scored ? `₹${Math.round(zz.theirTotal)}` : <span style={{ color: '#b45309' }} title={zz ? `only ${zz.matched}/${c.totalItems} items matched on Zomato` : 'no Zomato listing'}>{zz ? `unmatched (${Math.round((zz.coverage || 0) * 100)}%)` : 'no listing'}</span>}</td>
                  <td style={{ ...td, fontWeight: 700, color: scored ? dcol(zz.delta) : '#94a3b8' }}>{scored ? dstr(zz.delta) : '—'}</td>
                  <td style={{ ...td, color: '#64748B' }}>{scored && zz.bestOffer ? (zz.bestOffer.code || zz.bestOffer.text || '—') : '—'}</td>
                </tr>
              );
            })}
            {carts.length === 0 ? <tr><td style={td} colSpan={8}><span style={{ color: '#94a3b8' }}>No carts match this filter.</span></td></tr> : null}
          </tbody>
        </table>
      </div>
      {carts.length > limit ? <button onClick={() => setLimit(limit + 100)} style={{ ...s.btnGhost, marginTop: 8 }}>Show more ({carts.length - limit} more)</button> : null}

      <h4 style={{ fontSize: 14, fontWeight: 700, color: '#334155', margin: '20px 0 6px' }}>Summary by date</h4>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#F8FAFC', textAlign: 'left' }}>
              <th style={th}>Date (IST)</th>
              <th style={th}>Abandoned carts vs Zomato</th>
              <th style={th}>Placed carts vs Zomato</th>
            </tr>
          </thead>
          <tbody>
            <tr style={{ borderBottom: '2px solid #E2E8F0', background: '#FDFDEA' }}>
              <td style={{ ...td, fontWeight: 800 }}>All ({cc.byDate ? cc.byDate.length : 0}d)</td>
              <td style={td}>{cartAgg(ab)}</td>
              <td style={td}>{cartAgg(pl)}</td>
            </tr>
            {(cc.byDate || []).map((d) => (
              <tr key={d.date} style={{ borderBottom: '1px solid #EEF2F7' }}>
                <td style={{ ...td, fontWeight: 700, whiteSpace: 'nowrap' }}>{d.date}</td>
                <td style={td}>{cartAgg(d.zomato && d.zomato.abandoned)}</td>
                <td style={td}>{cartAgg(d.zomato && d.zomato.placed)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(cc.worstLosses || []).length ? (
        <div style={{ marginTop: 18 }}>
          <h4 style={{ fontSize: 14, fontWeight: 700, color: '#334155', margin: '0 0 6px' }}>Biggest losses — carts where we were most overpriced vs Zomato</h4>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#F8FAFC', textAlign: 'left' }}>
                  <th style={th}>Date</th><th style={th}>Outcome</th><th style={th}>Restaurant</th>
                  <th style={th}>Ours</th><th style={th}>Zomato</th><th style={th}>Δ</th><th style={th}>Their coupon</th>
                </tr>
              </thead>
              <tbody>
                {cc.worstLosses.map((x, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #EEF2F7', background: '#FFF7F7' }}>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{x.date}</td>
                    <td style={td}>{x.outcome === 'abandoned' ? <span style={{ color: '#b45309', fontWeight: 600 }}>abandoned</span> : 'placed'}</td>
                    <td style={{ ...td, fontWeight: 600 }}>{x.restaurantName}</td>
                    <td style={td}>₹{Math.round(x.ourTotal)}</td>
                    <td style={td}>₹{Math.round(x.theirTotal)}</td>
                    <td style={{ ...td, fontWeight: 700, color: '#dc2626' }}>{dstr(x.delta)}</td>
                    <td style={{ ...td, color: '#64748B' }}>{x.bestOffer ? (x.bestOffer.code || x.bestOffer.text || '—') : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
}
const th = { padding: '8px 10px', fontSize: 11, fontWeight: 700, color: '#475569', borderBottom: '2px solid #E2E8F0', whiteSpace: 'nowrap' };
const td = { padding: '8px 10px', verticalAlign: 'top' };

const s = {
  wrap: { maxWidth: 1000, margin: '0 auto', padding: 24, fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' },
  title: { fontSize: 24, fontWeight: 800, color: '#0F172A', margin: '0 0 6px' },
  sub: { color: '#64748B', fontSize: 14, margin: '0 0 18px', lineHeight: 1.5 },
  tabs: { display: 'flex', gap: 8, marginBottom: 16 },
  tab: { background: '#F1F5F9', border: '1px solid #E2E8F0', borderRadius: 8, padding: '8px 16px', fontSize: 14, fontWeight: 600, cursor: 'pointer', color: '#475569' },
  tabActive: { background: '#2563EB', color: '#fff', borderColor: '#2563EB' },
  card: { background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12, padding: 20 },
  row: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, padding: '14px 0', borderBottom: '1px solid #F1F5F9' },
  hint: { fontSize: 12, color: '#94A3B8', marginTop: 4, fontWeight: 400, lineHeight: 1.5 },
  chk: { width: 20, height: 20, cursor: 'pointer', flexShrink: 0 },
  input: { width: '100%', padding: '10px 12px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', marginTop: 8 },
  btn: { background: '#2563EB', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  btnGhost: { background: '#fff', color: '#2563EB', border: '1px solid #2563EB', borderRadius: 8, padding: '10px 16px', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  btnSm: { background: 'transparent', border: '1px solid #CBD5E1', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#334155' },
  candidate: { display: 'block', width: '100%', textAlign: 'left', background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 8, padding: '8px 12px', marginTop: 6, fontSize: 13, cursor: 'pointer', color: '#334155' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid #E2E8F0', color: '#64748B', fontWeight: 600, whiteSpace: 'nowrap' },
  td: { padding: '8px 10px', borderBottom: '1px solid #F1F5F9', color: '#334155' },
  thSm: { textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid #E2E8F0', color: '#94A3B8', fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap' },
  tdSm: { padding: '4px 8px', borderBottom: '1px solid #EEF2F7', color: '#475569', fontSize: 12 },
  thumb: { width: 34, height: 34, borderRadius: 4, objectFit: 'cover', flexShrink: 0, background: '#F1F5F9' },
  applyBtn: { background: '#0F766E', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
  badge: { fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, textTransform: 'uppercase' },
  badgeOk: { background: '#DCFCE7', color: '#166534' },
  badgeErr: { background: '#FEE2E2', color: '#B91C1C', cursor: 'help' },
  badgeNeutral: { background: '#F1F5F9', color: '#64748B' },
  warnBanner: { background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 10, padding: 12, fontSize: 13, color: '#92400E', marginBottom: 16, lineHeight: 1.5 },
  infoBanner: { background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 10, padding: 12, fontSize: 13, color: '#1E40AF', marginBottom: 16, lineHeight: 1.5 },
  okBanner: { background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 10, padding: 10, fontSize: 13, color: '#166534', marginBottom: 12, fontWeight: 600 },
  errBanner: { background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 10, padding: 10, fontSize: 13, color: '#B91C1C', marginBottom: 12 },
};

export default CompetitorIntel;
